import { JsonRpcResponse } from "../../core/types/rpc";
import { SuiCoinBalance } from "../../core/types/sui";

export class SuiRpcClient {
  /**
   * Native Sui coin object type in long format.
   * This is the format used by the Squid UI and API
   */
  private suiNativeCoinObjectTypeLong =
    "0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI";

  /**
   * Native Sui coin object type in short format.
   * This is the format used by RPC providers
   */
  private suiNativeCoinObjectTypeShort = "0x2::sui::SUI";

  private rpcUrl: string;

  constructor(rpcUrl: string) {
    this.rpcUrl = rpcUrl;
  }

  /**
   * Fetches the balance of a single Sui token for a given address.
   */
  async getBalance(
    userAddress: string,
    tokenAddress: string
  ): Promise<SuiCoinBalance> {
    const response = await this.call<SuiCoinBalance>("suix_getBalance", [
      userAddress,
      this.toShortCoinObjectType(tokenAddress),
    ]);

    return {
      ...response,
      coinType: this.toLongCoinObjectType(response.coinType),
    };
  }

  /**
   * Fetches all Sui coin balances for a given address.
   */
  async getAllBalances(userAddress: string): Promise<SuiCoinBalance[]> {
    const response = await this.call<SuiCoinBalance[]>("suix_getAllBalances", [
      userAddress,
    ]);

    return response.map((balance) => ({
      ...balance,
      coinType: this.toLongCoinObjectType(balance.coinType),
    }));
  }

  private async call<T>(method: string, params: any[]): Promise<T> {
    const response = await fetch(this.rpcUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method,
        params,
      }),
    });

    const data: JsonRpcResponse<T> = await response.json();

    if (data.error) {
      throw new Error(`RPC Error (${method}): ${data.error.message}`);
    }

    if (!data.result) {
      throw new Error(`Invalid response from RPC (${method})`);
    }

    return data.result;
  }

  /**
   * Parse native coin object type from the long format to the short format.
   */
  private toShortCoinObjectType(coinObjectType: string): string {
    return coinObjectType === this.suiNativeCoinObjectTypeLong
      ? this.suiNativeCoinObjectTypeShort
      : coinObjectType;
  }

  /**
   * Parse native coin object type from the short format to the long format.
   */
  private toLongCoinObjectType(coinObjectType: string): string {
    return coinObjectType === this.suiNativeCoinObjectTypeShort
      ? this.suiNativeCoinObjectTypeLong
      : coinObjectType;
  }
}
